import { useModal } from '@/contexts/modal'
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import DownLoadIcons from '@/icons/DownLoadIcons'
import EyeIcons from '@/icons/EyeIcons'
import LockIcons from '@/icons/LockIcons'
import { Attachment } from '@/modules/Upload/types'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton, Typogaphy } from 'vnetwork-ui'
import { getFileIcon } from '../Form/Shared/FileItem/shared/GenericFilePreview'

interface IContractAttachmentProps {
    label?: string
    attachments?: Attachment[]
    shouldCheckRegistrationDate?: boolean
    contractID?: number
    hideLabel?: boolean
}

const ContractAttachment: React.FC<IContractAttachmentProps> = ({
    label,
    attachments,
    shouldCheckRegistrationDate = false,
    contractID,
    hideLabel = false,
}) => {
    const { t } = useTranslation()
    const { openFilePreview, downloadFileWithPresigned } =
        useFilePreviewWithPresigned()
    const { setModal } = useModal()

    const formatFileSize = useCallback((bytes: number) => {
        if (bytes === 0) return '0 B'
        const k = 1024
        const sizes = ['B', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    }, [])

    const handlePreview = useCallback(
        async (attachment: Attachment) => {
            // If shouldCheckRegistrationDate is true, open the registration date modal first
            if (shouldCheckRegistrationDate && contractID) {
                setModal('ContractModals/RegistrationDateModal/index', {
                    contractID: contractID.toString(),
                    attachment: attachment, // Pass the attachment to modal
                    actionType: 'preview', // Specify this is for preview action
                    onSubmitSuccess: () => {
                        // File will be opened after modal closes and API call is done
                        // This is handled in the modal's onSuccess callback
                    },
                })
                return
            }

            // Normal preview flow when shouldCheckRegistrationDate is false
            await openFilePreview(attachment)
        },
        [shouldCheckRegistrationDate, contractID, setModal, openFilePreview]
    )

    const performDownload = useCallback(
        async (attachment: Attachment) => {
            await downloadFileWithPresigned(attachment)
        },
        [downloadFileWithPresigned]
    )

    const handleDownload = useCallback(
        async (attachment: Attachment) => {
            // If shouldCheckRegistrationDate is true, open the registration date modal first
            if (shouldCheckRegistrationDate && contractID) {
                setModal('ContractModals/RegistrationDateModal/index', {
                    contractID: contractID.toString(),
                    attachment: attachment, // Pass the attachment to modal
                    actionType: 'download', // Specify this is for download action
                    onSubmitSuccess: () => {
                        // File will be downloaded after modal closes and API call is done
                        // This is handled in the modal's onSuccess callback
                    },
                })
                return
            }

            // Normal download flow when shouldCheckRegistrationDate is false
            await performDownload(attachment)
        },
        [shouldCheckRegistrationDate, contractID, setModal, performDownload]
    )

    if (!attachments || attachments.length === 0) {
        return null
    }

    return (
        <>
            {!hideLabel && (
                <Typogaphy className="font-medium text-gray-700 mb-3">
                    {label || t('attachments')} ({attachments?.length})
                </Typogaphy>
            )}
            <div className="grid grid-cols-1 gap-2">
                {attachments.map((attachment, index) => (
                    <div
                        key={index}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <div className="relative w-8 h-8 bg-indigo-100 rounded flex items-center justify-center">
                            {getFileIcon(attachment?.attachment_file_type)}
                            {attachment?.attachment_closed && (
                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                    <LockIcons className="w-2.5 h-2.5 text-white" />
                                </div>
                            )}
                        </div>
                        <div className="flex-1">
                            <Typogaphy
                                size="small"
                                className="font-medium text-primary hover:cursor-pointer hover:underline"
                                onClick={() => handlePreview(attachment)}
                            >
                                {attachment?.attachment_file_name}
                            </Typogaphy>
                            <Typogaphy
                                size="small"
                                className="text-gray-500 text-xs mt-1"
                            >
                                {attachment?.attachment_file_size &&
                                    formatFileSize(
                                        attachment.attachment_file_size
                                    )}
                            </Typogaphy>
                        </div>
                        <div className="flex gap-2">
                            <IconButton
                                size="small"
                                onClick={() => handlePreview(attachment)}
                                className="text-primary hover:bg-indigo-100"
                            >
                                <EyeIcons />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => handleDownload(attachment)}
                                className="text-green-600 hover:bg-green-100"
                            >
                                <DownLoadIcons />
                            </IconButton>
                        </div>
                    </div>
                ))}
            </div>
        </>
    )
}

export default ContractAttachment
